
import { toast } from 'sonner';
import { AIPrediction } from './types';
import { checkTableExists, checkForCurrentWeekPredictions } from './database';
import { triggerPredictionsWebhook } from './webhook';
import { getPredictionsFromCache, updatePredictionsCache } from '@/lib/predictions/cache';
import { updateChartCache } from '@/lib/predictions/chartCache';
import { transformWebhookToPredictionCharts } from '@/lib/predictions/webhookTransformer';

export * from './types';

export const predictionsService = {
  async fetchLatestPredictions(): Promise<AIPrediction[]> {
    console.log('[PredictionsService] 🔍 Fetching latest predictions...');
    
    try {
      // Check database first
      const tableExists = await checkTableExists();
      
      if (!tableExists) {
        console.log('[PredictionsService] ❌ Table does not exist, triggering generation...');
        this.triggerNewPredictions();
        return [];
      }
      
      const currentWeekPredictions = await checkForCurrentWeekPredictions();
      
      if (currentWeekPredictions && currentWeekPredictions.length > 0) {
        console.log('[PredictionsService] 📊 Found predictions in database:', currentWeekPredictions.length);
        
        // If we have chart data in database, update the chart cache
        const goldPrediction = currentWeekPredictions.find(p => p.metal === 'ORO');
        const silverPrediction = currentWeekPredictions.find(p => p.metal === 'ARGENTO');
        
        if ((goldPrediction?.time_series && goldPrediction?.scenarios) || 
            (silverPrediction?.time_series && silverPrediction?.scenarios)) {
          console.log('[PredictionsService] 📈 Updating chart cache from database data');
          
          const chartData = {
            gold: goldPrediction?.time_series ? {
              metal: 'ORO' as const,
              currentPrice: goldPrediction.current_price || 0,
              targetPrice: goldPrediction.target_price || 0,
              targetPercentage: goldPrediction.target_percentage || 0,
              timeframe: goldPrediction.timeframe || '',
              confidence: goldPrediction.confidence_score,
              timeSeries: goldPrediction.time_series,
              scenarios: goldPrediction.scenarios
            } : null,
            silver: silverPrediction?.time_series ? {
              metal: 'ARGENTO' as const,
              currentPrice: silverPrediction.current_price || 0,
              targetPrice: silverPrediction.target_price || 0,
              targetPercentage: silverPrediction.target_percentage || 0,
              timeframe: silverPrediction.timeframe || '',
              confidence: silverPrediction.confidence_score,
              timeSeries: silverPrediction.time_series,
              scenarios: silverPrediction.scenarios
            } : null
          };
          
          updateChartCache(chartData);
        }
        
        // Update text predictions cache
        updatePredictionsCache(currentWeekPredictions);
        return [...currentWeekPredictions];
      }
      
      // If no predictions, generate in background
      console.log('[PredictionsService] 🔄 No predictions found, triggering generation in background');
      this.triggerNewPredictions();
      return [];
    } catch (err) {
      console.error('[PredictionsService] ❌ Error:', err);
      // Generate in background even on error
      this.triggerNewPredictions();
      return [];
    }
  },

  triggerNewPredictions: triggerPredictionsWebhook
};
